import { TableQueryFilter, ZomentumEntities } from "../common";
import {
	SalesActivityAutomationActionUpdatableField,
	SalesActivityAutomationMetadataFilter
} from "../Others/SalesActivity";
import { SyncSource } from "../Settings";
import { ChildEntityHydrationParams } from "../Utils";

export type CustomFiltersMetaData = Omit<
	SalesActivityAutomationMetadataFilter,
	| "filter_operator"
	| "values"
	| "filter_popover_visible"
	| "filter_search_criteria"
>;

export type CustomViewFilterCriteria = Omit<
	SalesActivityAutomationMetadataFilter,
	| "valid_filter_operators"
	| "valid_values"
	| "value_type"
	| "filter_popover_visible"
	| "filter_search_criteria"
>;
export enum CustomViewColumnStatus {
	Enabled = "enabled",
	Disabled = "disabled",
	AlwaysEnabled = "always_enabled"
}

export interface CustomViewColumns {
	system_field: string | null;
	display_name: string;
	custom_field_id: string | null;
	view_column_status: CustomViewColumnStatus;
	is_always_enabled: boolean;
	is_sortable: boolean;
}

export class CustomView {
	id = "";
	name = "";
	company_id = "";
	entity_type: ZomentumEntities = ZomentumEntities.Opportunity;
	filter_criteria: CustomViewFilterCriteria[] =
		new Array<CustomViewFilterCriteria>();
	columns: CustomViewColumns[] = new Array<CustomViewColumns>();
	is_system_view = false;
	query_string?: string;
}

export interface CustomViewResponse {
	data: CustomView[];
	filtered_count_map: null | any;
}

export interface SystemFieldColumn {
	field_display_name: string;
	field_name: string; // could be keyof Doc, Opp, Client
	view_column_status: CustomViewColumnStatus;
}

export interface CustomViewsMetadata {
	readonly columns: CustomViewColumns[];
	readonly filters: CustomFiltersMetaData[];
}

export enum SortCriterionOrder {
	Ascending = "asc",
	Descending = "desc"
}

export interface SortCriterion {
	system_field: string | null;
	order: SortCriterionOrder | "";
	custom_field_id: string | null;
}

export class ListPagePostRequest {
	readonly count_query_flag: boolean;
	readonly filter_criterion = new Array<CustomViewFilterCriteria>();
	readonly sort_criterion: SortCriterion[] = [];
	readonly page_no: number = 1;
	readonly page_size: number = 10;
	readonly included_child_entities: ChildEntityHydrationParams[] =
		new Array<ChildEntityHydrationParams>();
	readonly show_archived?: boolean;
	constructor(args: {
		count_query_flag: boolean;
		filter_criterion: CustomViewFilterCriteria[];
		sort_criterion: SortCriterion[];
		page_no: number;
		page_size: number;
		includedChildEntities: ChildEntityHydrationParams[];
		showArchived?: boolean;
	}) {
		this.count_query_flag = args.count_query_flag;
		this.filter_criterion = args.filter_criterion;
		this.sort_criterion = args.sort_criterion;
		this.page_no = args.page_no;
		this.page_size = args.page_size;
		this.included_child_entities = args.includedChildEntities;
		this.show_archived = args.showArchived;
	}
}

export enum BulkActions {
	Delete = "delete",
	Update = "update",
	Export = "export"
}

export class BulkDeleteRecordsRequest {
	async_operation_type: BulkActions = BulkActions.Update;
	metadata: BulkDeleteMetadataRequest = new BulkDeleteMetadataRequest();
}
export class BulkUpdateRecordsRequest {
	async_operation_type: BulkActions = BulkActions.Update;
	metadata: BulkUpdateMetadataRequest = new BulkUpdateMetadataRequest();
}

export class BulkExportRecordsRequest {
	async_operation_type: BulkActions = BulkActions.Export;
	metadata: BulkExportMetadataRequest = new BulkExportMetadataRequest();
}

export class BulkDeleteMetadataRequest {
	filters: CustomViewFilterCriteria[] = new Array<CustomViewFilterCriteria>();
	entity_type: ZomentumEntities = ZomentumEntities.Opportunity;
}

export class BulkUpdateMetadataRequest {
	filters: CustomViewFilterCriteria[] = new Array<CustomViewFilterCriteria>();
	entity_type: ZomentumEntities = ZomentumEntities.Opportunity;
	fields?: SalesActivityAutomationActionUpdatableField[] =
		new Array<SalesActivityAutomationActionUpdatableField>();
}

export class BulkExportMetadataRequest {
	filters: CustomViewFilterCriteria[] = new Array<CustomViewFilterCriteria>();
	entity_type: ZomentumEntities = ZomentumEntities.Opportunity;
}

export class IntegrationPageDetailsUpdateRequest {
	id = "";
	integrations_page_details: IntegrationPageDetailsUpdateData =
		new IntegrationPageDetailsUpdateData();
}

export class IntegrationPageDetailsUpdateData {
	favourite_integrations: string[] = [];
	integrations_page_visited = false;
}

export enum BulkOperationStatus {
	Initiated = "initiated",
	InProgress = "in_progress",
	Complete = "complete",
	TimedOut = "timed_out",
	Failed = "failed"
}

export interface BulkActionFailureMetadata {
	entity_id: string;
	entity_name: string;
	entity_identifier?: string;
	entity_identifier_id?: string;
	failure_message: string;
}

export interface BulkUpdatesActionResult {
	id: string;
	company_id: string;
	user_id: string;
	operation_status: BulkOperationStatus;
	operation_started_at: Date;
	operation_completed_at: Date;
	metadata: {
		entity_type?: ZomentumEntities;
		ids_matched?: string[];
		failed_ids?: string[];
		distributor?: string;
		connection_type?: string;
		url?: string;
		updated_row_count?: number;
		failed_row_count?: number;
		failure_messages?: string[];
		sync_source?: SyncSource;
		updated_clients_count?: number;
		updated_opportunities_count?: number;
		updated_contacts_count?: number;
		updated_items_count?: number;
		failures: BulkActionFailureMetadata[];
		download_url?: string;
	};
}

export interface BulkUpdatesActionResultResponse {
	data: BulkUpdatesActionResult[];
	filtered_count_map: null | { all: number };
}

export class BulkUpdatesResultRequest {
	limit = 10;
	filters: TableQueryFilter[] = [];
	page = 1;
}

export interface GetCollectJSTokenResponse {
	token: string | null;
}

export type MissingClientContactType = {
	name: string;
	id: string;
};

export enum ViewType {
	LIST_VIEW = "LIST_VIEW",
	GRID_VIEW = "GRID_VIEW"
}
